/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — React Flow node renderers (person / team-box /
   junction) + the palette drag tokens. Behavior-ported from the old
   `nodes.tsx`, EXPANDED with the granted node customization: shape
   (rectangle/circle avatar-only), density (expanded/compact), badge
   toggles, and the ≥400ms hover profile card.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import { Handle, Position, type Node, type NodeProps, type NodeTypes } from '@xyflow/react';
import { Star } from 'lucide-react';
import type { TeamMember, Team } from '../../../../shared/types';
import type { NoteNodeData, PersonNodeData } from './types';
import { ProfileCard } from './ProfileCard';

// People Rebuild §3 restore (Josh, 2026-08-19): "hover for details in view
// mode, not click" — the hover profile card already existed and worked;
// tuned to his literal ~300ms ask (was 400ms) and made keyboard-reachable
// (focus/blur mirror mouse enter/leave below) since the original spec
// promised a keyboard-accessible equivalent that was never wired.
const HOVER_DELAY_MS = 300;

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function PersonNode({ data }: NodeProps<Node<PersonNodeData>>) {
  const {
    member,
    teamColor,
    pills: allPills,
    isLeader,
    companyBadge,
    glow,
    nodeStyle,
    canViewBilling,
    canViewRoles,
    roleLabel,
    teamMemberships,
  } = data;
  const initials = getInitials(member.name);
  const color = teamColor || 'var(--ac)';
  const pills = allPills.filter(
    (p) =>
      (p.kind === 'department' && nodeStyle.showDepartmentPill) ||
      (p.kind === 'team' && nodeStyle.showTeamPills)
  );
  const showLeaderBadge = isLeader && nodeStyle.showLeaderStar;
  const showTitleBadge = !!companyBadge && nodeStyle.showTitleBadge;
  const compact = nodeStyle.density === 'compact';
  const circle = nodeStyle.shape === 'circle';

  const wrapRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showCard, setShowCard] = useState(false);
  const onEnter = () => {
    hoverTimer.current = setTimeout(() => setShowCard(true), HOVER_DELAY_MS);
  };
  const onLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setShowCard(false);
  };

  // Keyboard-accessible equivalent (2026-08-19 hover restore): the
  // FOCUSABLE element is the ancestor `.react-flow__node` React Flow
  // itself wires tabIndex/role onto (the on-canvas a11y hint already says
  // "Press enter or space to select a node... arrow keys to move it") —
  // not this inner div, so a React onFocus/onBlur prop here would never
  // fire. Attach native focus/blur to that ancestor instead, mirroring
  // mouse enter/leave with the same delay + timer cleanup.
  useEffect(() => {
    const el = wrapRef.current?.closest('.react-flow__node');
    if (!el) return;
    el.addEventListener('focus', onEnter);
    el.addEventListener('blur', onLeave);
    return () => {
      el.removeEventListener('focus', onEnter);
      el.removeEventListener('blur', onLeave);
    };
  }, []);

  // Card dims ~20% larger in every mode (Josh, print legibility grant
  // 2026-08-19) — avatar/typography/badges below scale together so the
  // card reads as "bigger", not "bigger empty box". Old: circle 44/56,
  // rect 28/36.
  const avatarSize = circle ? (compact ? 52 : 68) : compact ? 34 : 44;

  return (
    <div
      ref={wrapRef}
      className="person-node"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ position: 'relative' }}
    >
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <div className="handle-dot handle-dot-top" />
      <div className="handle-dot handle-dot-bottom" />
      <div className="handle-dot handle-dot-left" />
      <div className="handle-dot handle-dot-right" />

      {circle ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 5,
            cursor: 'grab',
            userSelect: 'none',
            opacity: member.active ? 1 : 0.5,
          }}
        >
          <div
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: '50%',
              background: color + '18',
              border: `2px solid ${color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              boxShadow: glow
                ? '0 0 0 3px color-mix(in srgb, var(--ac) 45%, transparent), 0 2px 8px rgba(0,0,0,.08)'
                : '0 2px 8px rgba(0,0,0,.08)',
              transition: 'box-shadow 120ms ease',
            }}
          >
            <span style={{ fontSize: avatarSize * 0.32, fontWeight: 700, color }}>{initials}</span>
            {(showLeaderBadge || showTitleBadge) && (
              <div style={{ position: 'absolute', top: -7, right: -7, display: 'flex', gap: 2 }}>
                {showLeaderBadge && <LeaderBadge color={color} />}
              </div>
            )}
            {showTitleBadge && companyBadge && (
              <span
                style={{
                  position: 'absolute',
                  bottom: -7,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: 11,
                  fontWeight: 800,
                  padding: '1px 5px',
                  borderRadius: 4,
                  lineHeight: 1,
                  background: 'var(--sf)',
                  color: companyBadge.color,
                  border: `1.5px solid ${companyBadge.color}`,
                  whiteSpace: 'nowrap',
                }}
              >
                {companyBadge.code}
              </span>
            )}
          </div>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--tx)',
              maxWidth: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            {member.name}
          </span>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 10 : 12,
            padding: compact ? '7px 12px' : '12px 16px',
            background: 'var(--sf)',
            border: `2px solid ${color}`,
            borderRadius: 10,
            minWidth: 144,
            boxShadow: glow
              ? '0 0 0 3px color-mix(in srgb, var(--ac) 45%, transparent), 0 2px 8px rgba(0,0,0,.08)'
              : '0 2px 8px rgba(0,0,0,.08)',
            cursor: 'grab',
            userSelect: 'none',
            opacity: member.active ? 1 : 0.5,
            position: 'relative',
            transition: 'box-shadow 120ms ease',
          }}
        >
          {(showLeaderBadge || showTitleBadge) && (
            <div
              style={{
                position: 'absolute',
                top: -10,
                right: -5,
                display: 'flex',
                flexDirection: 'row-reverse',
                gap: 2,
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              {showLeaderBadge && <LeaderBadge color={color} />}
              {showTitleBadge && companyBadge && (
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 800,
                    padding: '2px 6px',
                    borderRadius: 4,
                    lineHeight: 1,
                    background: 'var(--sf)',
                    color: companyBadge.color,
                    border: `1.5px solid ${companyBadge.color}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,.12)',
                  }}
                >
                  {companyBadge.code}
                </span>
              )}
            </div>
          )}
          <div
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: '50%',
              flexShrink: 0,
              background: color + '18',
              border: `2px solid ${color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: avatarSize * 0.36, fontWeight: 700, color }}>{initials}</span>
          </div>
          {!compact && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--tx)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {member.name}
              </div>
              {pills.length > 0 && (
                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 1 }}>
                  {pills.map((p) => (
                    <span
                      key={`${p.kind}:${p.label}`}
                      title={
                        p.kind === 'department' ? `Department: ${p.label}` : `Team: ${p.label}`
                      }
                      style={{
                        fontSize: 14,
                        padding: '1px 6px',
                        borderRadius: 4,
                        fontWeight: 600,
                        background: p.color + '18',
                        color: p.color,
                        border: `1px solid ${p.color}40`,
                      }}
                    >
                      {p.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {compact && (
            <span
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--tx)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 120,
              }}
            >
              {member.name}
            </span>
          )}
        </div>
      )}

      {showCard &&
        wrapRef.current &&
        (() => {
          const r = wrapRef.current!.getBoundingClientRect();
          return (
            <ProfileCard
              member={member}
              teams={teamMemberships}
              roleLabel={roleLabel}
              titleLabel={companyBadge?.code}
              anchorRect={r}
              canViewBilling={canViewBilling}
              canViewRoles={canViewRoles}
            />
          );
        })()}
    </div>
  );
}

// Exported so the legend (LegendPopover + the print CaptureSheet) can
// render the EXACT same badge markup instead of a hand-drawn stand-in —
// a hand-drawn duplicate is how the legend's leader entry drifted from
// what cards actually show (Josh bug report 2026-08-19: "the team leader
// badge on the legend is wrong" — the print legend had no star glyph at
// all, and both legends used a fixed accent color no real card uses,
// since a card's badge is always tinted by ITS team's color).
export function LeaderBadge({ color }: { color: string }) {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,.2)',
      }}
    >
      <Star
        size={11}
        fill="var(--color-text-inverse)"
        color="var(--color-text-inverse)"
        aria-hidden="true"
      />
    </div>
  );
}

// ─── TeamBoxNode: backdrop for visual peer-grouping ───
export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function TeamBoxNode({
  data,
}: NodeProps<
  Node<{
    name: string;
    color: string;
    width: number;
    height: number;
    depth?: number;
    /** Team Management UX Overhaul (2026-08-18): live drag-legibility hint
     *  — set for one frame at a time by OrgChartV2's team-box derivation
     *  effect while a person card or another team box is being dragged
     *  over/out of this one. 'add' = a card not yet on this team is
     *  hovering it; 'remove' = a card that IS on this team has been
     *  dragged out of it; 'reparent' = another team box is fully inside
     *  this one. All resolve on release ("… on save" toast) — nothing
     *  writes until Save. */
    dragHint?: 'add' | 'remove' | 'reparent';
  }>
>) {
  const { name, color, width, height, depth = 0, dragHint } = data;
  const hintColor = dragHint === 'remove' ? 'var(--dg)' : color;
  // Nested Team Containers (People Rebuild): stacked translucent tints
  // read as mud once a box sits inside another box — depth >= 1 (i.e.
  // any team with a parent) gets a SOLID fill (team color mixed over
  // the surface token) instead of the 6% tint. Top-level boxes are
  // untouched.
  const background = dragHint
    ? `color-mix(in srgb, ${hintColor} 30%, var(--sf))`
    : depth >= 1
      ? `color-mix(in srgb, ${color} 18%, var(--sf))`
      : hexToRgba(color, 0.06);
  return (
    <div
      style={{
        width,
        height,
        border: dragHint ? `3px solid ${hintColor}` : `2px dashed ${color}`,
        borderRadius: 16,
        background,
        position: 'relative',
        pointerEvents: 'none',
        boxShadow: dragHint
          ? `0 0 0 4px color-mix(in srgb, ${hintColor} 24%, transparent)`
          : undefined,
        transition: 'background 100ms ease, border 100ms ease, box-shadow 100ms ease',
      }}
    >
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <div
        style={{
          position: 'absolute',
          top: -10,
          left: 16,
          background: 'var(--sf)',
          padding: '1px 8px',
          fontSize: 11,
          fontWeight: 600,
          color,
          border: `1px solid ${color}`,
          borderRadius: 999,
          letterSpacing: 0.2,
        }}
      >
        {name}
      </div>
      {dragHint && (
        <div
          style={{
            position: 'absolute',
            top: -10,
            right: 16,
            background: hintColor,
            padding: '2px 10px',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--color-text-inverse)',
            borderRadius: 999,
            letterSpacing: 0.2,
            whiteSpace: 'nowrap',
          }}
        >
          {dragHint === 'add'
            ? `+ Add to ${name}`
            : dragHint === 'remove'
              ? `− Remove from ${name}`
              : `Nest under ${name}`}
        </div>
      )}
    </div>
  );
}

// ─── JunctionNode: multi-way connection hub ───
export function JunctionNode({
  data,
  selected,
}: NodeProps<Node<{ label?: string; color?: string; glow?: boolean }>>) {
  const color = data.color || 'var(--ac)';
  return (
    <div
      style={{ width: 16, height: 16, position: 'relative', transform: 'translate(-2px, -2px)' }}
    >
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: color,
          border: `2px solid ${selected ? 'var(--tx)' : 'white'}`,
          boxShadow: data.glow
            ? '0 0 0 4px color-mix(in srgb, var(--ac) 45%, transparent)'
            : selected
              ? `0 0 0 2px ${color}`
              : '0 1px 2px rgba(0,0,0,0.15)',
          margin: 2,
          cursor: 'move',
          transition: 'box-shadow 120ms ease',
        }}
      />
      {data.label && (
        <div
          style={{
            position: 'absolute',
            top: 18,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--sf)',
            border: `1px solid ${color}`,
            borderRadius: 999,
            padding: '1px 8px',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            color: 'var(--tx)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {data.label}
        </div>
      )}
    </div>
  );
}

// ─── TextBoxNode: free-floating text note (Josh grant 2026-08-17) ───
// Content renders in BOTH view and edit mode; the dashed border and the
// inline-edit textarea are session-only affordances (canEdit gates
// both). No connect handles — notes aren't edge endpoints.
export function TextBoxNode({ id, data }: NodeProps<Node<NoteNodeData>>) {
  const { text, fontSize, color, editing, canEdit, onCommitText, onCancelEdit } = data;
  const [draft, setDraft] = useState(text);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // `id` here is the REACT FLOW node id (`note-<uuid>`, see buildNoteNode),
  // but every consumer of onCommitText/onCancelEdit (handleCommitNoteText,
  // sessionUpdateNote, editingNoteId/noteMenu.noteId elsewhere in
  // OrgChartV2.tsx) works in RAW note ids and re-adds its own `note-`
  // prefix internally. Passing the already-prefixed id through unstripped
  // double-prefixed the lookup (`note-note-<uuid>`), which matched
  // nothing — so sessionUpdateNote silently no-opped and every typed edit
  // was discarded on commit, reverting to the pre-edit text (bug report
  // 2026-08-19: "I can't type in text boxes" — keystrokes DID register,
  // they just never survived blur/commit). Strip the prefix here so this
  // node matches the raw-id convention every other call site already
  // uses.
  const rawId = id.replace(/^note-/, '');

  useEffect(() => {
    if (editing) setDraft(text);
  }, [editing, text]);

  useEffect(() => {
    if (editing) taRef.current?.focus();
  }, [editing]);

  const commit = () => onCommitText?.(rawId, draft);
  const cancel = () => onCancelEdit?.(rawId);

  if (editing) {
    return (
      <div style={{ padding: '4px 8px' }}>
        <textarea
          ref={taRef}
          aria-label="Text box content"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
            e.stopPropagation();
          }}
          style={{
            fontSize,
            color: color || 'var(--tx)',
            fontFamily: 'inherit',
            fontWeight: 500,
            background: 'var(--sf)',
            border: '1px solid var(--ac)',
            borderRadius: 6,
            padding: '4px 8px',
            minWidth: 100,
            minHeight: fontSize * 1.6,
            resize: 'both',
            outline: 'none',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '4px 8px',
        fontSize,
        fontWeight: 500,
        color: color || 'var(--tx)',
        fontFamily: 'inherit',
        background: 'transparent',
        border: canEdit ? '1px dashed var(--bd)' : '1px solid transparent',
        borderRadius: 6,
        whiteSpace: 'pre-wrap',
        minWidth: 24,
        minHeight: fontSize * 1.4,
        cursor: canEdit ? 'grab' : 'default',
        userSelect: 'none',
      }}
    >
      {text || (canEdit ? 'Text' : '')}
    </div>
  );
}

export const nodeTypes: NodeTypes = {
  person: PersonNode,
  teamBox: TeamBoxNode,
  junction: JunctionNode,
  textBox: TextBoxNode,
};

// ─── Palette drag tokens ───
export function MemberToken({
  member,
  color,
  size = 40,
  style: extra,
  ...rest
}: {
  member: TeamMember;
  color: string;
  size?: number;
  style?: React.CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      title={member.name}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: color + '20',
        border: `2px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'grab',
        userSelect: 'none',
        position: 'relative',
        ...extra,
      }}
    >
      <span style={{ fontSize: size * 0.35, fontWeight: 700, color, fontFamily: 'inherit' }}>
        {getInitials(member.name)}
      </span>
    </div>
  );
}

export function TeamToken({
  team,
  size = 36,
  style: extra,
  ...rest
}: {
  team: Team;
  size?: number;
  style?: React.CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      title={`Drag onto a person to assign them to ${team.name}`}
      style={{
        height: size,
        padding: '0 10px',
        borderRadius: 6,
        flexShrink: 0,
        background: (team.color || 'var(--ac)') + '18',
        border: `2px solid ${team.color || 'var(--ac)'}`,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        cursor: 'grab',
        userSelect: 'none',
        ...extra,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: team.color || 'var(--ac)',
          whiteSpace: 'nowrap',
        }}
      >
        {team.name}
      </span>
    </div>
  );
}
